import { useCallback, useEffect, useState } from 'react';
import { conversationStartersConversationStartersGet as getConversationStartersFromApi } from 'Assistant/__generated__/client';
import { ConversationStarter } from 'Assistant/__generated__/client.schemas';
import { useAuthHeader } from 'Assistant/Chatbot/hooks/useAuthHeader';
import { logException } from 'App/logging';

function useConversationStarters() {
  const [isLoadingConversationStarters, setIsLoadingConversationStarters] =
    useState(false);
  const [
    errorFetchingConversationStarters,
    setErrorFetchingConversationStarters,
  ] = useState(false);
  const [conversationStarters, setConversationStarters] = useState<
    ConversationStarter[]
  >([]);
  const accessTokenHeader = useAuthHeader();

  const getConversationStarters = useCallback(async () => {
    setIsLoadingConversationStarters(true);
    setErrorFetchingConversationStarters(false);
    try {
      const { data, status } = await getConversationStartersFromApi({
        headers: {
          'client-platform': 'web',
          ...accessTokenHeader,
        },
      });
      // We need to match specific status codes to help type inference. We also
      // match >= 400 defensively, in case we receive an error code which is not
      // specified in the generated schema.
      if (status === 401 || status === 403 || status === 503 || status >= 400) {
        throw new Error(
          `Failed to fetch conversation starters, status-code: ${status}`,
        );
      }

      setConversationStarters(data);
      setIsLoadingConversationStarters(false);
    } catch (error) {
      setIsLoadingConversationStarters(false);
      setErrorFetchingConversationStarters(true);

      // Extract status code from error if available
      const errorObj = error as {
        response?: { status?: number };
        status?: number;
        message?: string;
      };
      const statusCode =
        errorObj?.response?.status ||
        errorObj?.status ||
        errorObj?.message?.match(/status-code: (\d+)/)?.[1];

      logException(error, {
        tags: {
          product: 'nova',
          feature: 'nova-chat',
          action: 'fetch-conversation-starters',
          errorType: statusCode ? 'network' : 'unknown',
        },
        extra: {
          hasAuthHeader: !!accessTokenHeader,
          statusCode: statusCode || 'unknown',
        },
      });
    }
  }, [accessTokenHeader]);

  useEffect(() => {
    if (accessTokenHeader) {
      void getConversationStarters();
    }
  }, [accessTokenHeader, getConversationStarters]);

  return {
    conversationStarters,
    isLoadingConversationStarters,
    errorFetchingConversationStarters,
    refetchConversationStarters: getConversationStarters,
  };
}

export { useConversationStarters };
